//NODE MODULES
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useLazyQuery, gql } from "@apollo/client";
import _ from "lodash";
import { useAtomValue } from "jotai";

//KPP COMPONENTS
import { GridItemLink, PageHeader } from "../components/helpers";
import { NewsItem } from "../components/news";

//STATE
import { previewContentAtom } from "../state/appState";

//GRAPHQL
import { GET_NEWS } from "../graphql/news";
const QUERY_LIMIT = 17;

const News = () => {
	const observer = useRef(null);
	const urlParams = useParams();
	const loadMoreTrigger = useRef();

	//Local State
	const [querySkip, setQuerySkip] = useState(0);
	const [showMore, setShowMore] = useState(false);
	const [news, setNews] = useState([]);

	//Atom State
	const isPreviewContent = useAtomValue(previewContentAtom);

	//GraphQL Setup
	const [getNews, { data, errors, loading }] = useLazyQuery(GET_NEWS, {
		variables: {
			queryLimit: QUERY_LIMIT,
			querySkip: querySkip,
			isPreview: isPreviewContent,
		},
	});

	useEffect(() => {
		getNews();
	}, []);

	useEffect(() => {
		let newsData = [];

		if (data && data.newsCollection) {
			newsData = data.newsCollection.items;
		}

		if (data && newsData.length < QUERY_LIMIT) {
			setShowMore(false);
			if (loadMoreTrigger.current) observer?.current?.unobserve(loadMoreTrigger.current);
		} else if (data) {
			setShowMore(true);
		}

		setNews((prevState) => [...prevState, ...newsData]);
	}, [data]);

	useEffect(() => {
		if (showMore) {
			//create new instance and pass a callback function
			observer.current = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						const intersecting = entry.isIntersecting;
						if (intersecting) {
							// console.log("Load More...");
							getNews();
							setQuerySkip((prevState) => prevState + QUERY_LIMIT);
						}
					});
				},
				{ rootMargin: "-200px" }
			);

			//Get the trigger element and observe it
			observer.current.observe(loadMoreTrigger.current);
		}

		//Cleanup function to remove observer
		return () => {
			if (loadMoreTrigger.current) observer?.current?.unobserve(loadMoreTrigger.current);
		};
	}, [showMore]);

	return (
		<>
			<PageHeader />
			<div className="grid-wrapper pageHeader-wrapper aItems-center">
				<div id="pageHeader-title">
					<h1>Latest News</h1>
				</div>
				<div id="pageHeader-desc">
					<p className="text-maxWidth"></p>
				</div>
			</div>
			{/* <div className="grid-wrapper projects-wrapper" style={{ minHeight: "90vh" }}> */}
			<div className="grid-wrapper grid-columns-autoFill projects-wrapper">
				{news.length ? (
					news.map((newsItem, idx) => {
						const delay = 200 * (idx / ((querySkip + QUERY_LIMIT) / QUERY_LIMIT) + 1);
						return (
							<NewsItem
								key={`newsItem-${newsItem.sys.id}`}
								id={newsItem.sys.id}
								slug={newsItem.slug}
								title={newsItem.title}
								image={newsItem.heroImage.url}
								date={newsItem.altPublishDate || newsItem.sys.firstPublishedAt}
								project={newsItem.project}
								animationDelay={delay}
							/>
						);
					})
				) : (
					<p>No News at the moment, please check back soon to stay up to date with KPP.</p>
				)}

				<GridItemLink linkTo="/news/archive" label="View Archived News" icon="bi bi-archive" />
			</div>

			{loading && <div className="loadingSpinner"></div>}
			{showMore && <div id="loadMore" ref={loadMoreTrigger}></div>}
		</>
	);
};

export default News;
